import { forwardRef, useEffect, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Bookmark, Building2, Heart, Link2, MapPin, Search } from 'lucide-react'
import { toast } from 'sonner'

import { Job } from '@/_gql/graphql'

import { useAuth } from '@/services/auth/use-auth'
import { GraphQLError } from '@/services/graphql/error'
import { gqlFetch } from '@/services/graphql/fetcher'
// Import GraphQL queries and utilities
import { GqlBookmarkJob } from '@/services/job/gql'
import { queryJobList } from '@/services/job/query'

import { toIdrWithoutRp } from '@/lib/utils/number'

interface SavedJobsListProps {
  ref?: HTMLDivElement | null
  id?: string
}

// Enhanced Job interface with bookmark status
interface EnhancedJob extends Omit<Job, 'is_bookmark'> {
  is_bookmark: boolean
}

// Bookmark mutation hook (reused from original)
const useUpdateJobBookmark = () => {
  const queryClient = useQueryClient()
  const [isDialogErrorOpen, setIsDialogErrorOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')

  const { mutateAsync: updateJobBookmark, isPending: isUpdatingBookmark } =
    useMutation({
      mutationFn: async ({
        job_id,
        value,
      }: {
        job_id: number
        value: boolean
      }) => {
        const result = await gqlFetch({
          query: GqlBookmarkJob,
          variables: {
            jobId: job_id,
            value: value,
          },
        })
        return { job_id, value, result }
      },
      onSuccess: (data) => {
        const message = data.value
          ? 'Pekerjaan berhasil disimpan!'
          : 'Pekerjaan dihapus dari simpanan!'
        toast.success(message)

        // Invalidate queries to refresh the saved jobs list
        queryClient.invalidateQueries({ queryKey: ['jobList'] })
        queryClient.invalidateQueries({ queryKey: ['savedJobs'] })
      },
      onError: (error) => {
        let msg = error.message
        if (GraphQLError.is(error)) {
          msg = error.getMessage()
        }
        toast.error(msg)
      },
    })

  return {
    updateJobBookmark,
    isPending: isUpdatingBookmark,
    isDialogErrorOpen,
    setIsDialogErrorOpen,
    dialogMessage,
  }
}

// Loading skeleton component for saved jobs
const SavedJobCardSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-[#D4D4D4] bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center gap-4">
      <div className="h-12 w-12 rounded-lg bg-gray-200"></div>
      <div className="flex-1">
        <div className="mb-2 h-5 rounded bg-gray-200"></div>
        <div className="h-4 w-24 rounded bg-gray-200"></div>
      </div>
    </div>
    <div className="mb-4 flex gap-2">
      <div className="h-6 w-16 rounded-xl bg-gray-200"></div>
      <div className="h-6 w-20 rounded-xl bg-gray-200"></div>
    </div>
    <div className="mb-4 flex items-center gap-2">
      <div className="h-4 w-4 rounded bg-gray-200"></div>
      <div className="h-4 w-32 rounded bg-gray-200"></div>
    </div>
    <div className="h-4 w-40 rounded bg-gray-200"></div>
  </div>
)

// Saved Job Card Component (structured layout as requested)
const SavedJobCard = ({ job }: { job: EnhancedJob }) => {
  const navigate = useNavigate()
  const [localBookmarkState, setLocalBookmarkState] = useState(job.is_bookmark)
  const {
    updateJobBookmark,
    isPending: isUpdatingBookmark,
    isDialogErrorOpen,
    setIsDialogErrorOpen,
    dialogMessage,
  } = useUpdateJobBookmark()

  // Update local state when job prop changes
  useEffect(() => {
    setLocalBookmarkState(job.is_bookmark)
  }, [job.is_bookmark])

  const handleJobClick = () => {
    navigate({
      to: `/job-detail/${job.id}`,
    })
  }

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation()
    const jobUrl = `${window.location.origin}/job-detail/${job.id}`
    navigator.clipboard.writeText(jobUrl)
    toast.success('Link berhasil disalin!')
  }

  const handleRemoveFromSaved = async (e: React.MouseEvent) => {
    e.stopPropagation()

    // Optimistic update - immediately update UI
    setLocalBookmarkState(false)

    try {
      // Remove bookmark
      await updateJobBookmark({ job_id: job.id, value: false })
    } catch (error) {
      // Revert optimistic update on error
      setLocalBookmarkState(true)
      console.error('Failed to remove bookmark:', error)
    }
  }

  const handleApplyNow = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate({
      to: `/job-detail/${job.id}`,
    })
  }

  return (
    <>
      <div className="flex h-[25em] flex-col justify-between rounded-lg border border-[#D4D4D4] bg-white shadow-sm transition-all duration-300 hover:border-primary-yes hover:shadow-xl hover:shadow-[#D91F2E]/30">
        {/* Top Section - Badge, Bookmark, and Copy Link */}
        <div className="flex items-center justify-between p-4 pb-0">
          {/* Saved Badge */}
          <div className="rounded-full bg-primary-yes px-3 py-1 text-xs font-medium text-white">
            Tersimpan
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Remove from Saved Button */}
            <button
              onClick={handleRemoveFromSaved}
              className="rounded-full bg-primary-yes p-2 text-white transition-all duration-300 hover:bg-red-600"
              title="Hapus dari simpanan"
              disabled={isUpdatingBookmark}
            >
              <Bookmark size={16} className="fill-current" />
            </button>

            {/* Copy Link Button */}
            <button
              onClick={handleCopyLink}
              className="rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-primary-yes hover:text-white"
              title="Salin link"
            >
              <Link2 size={16} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div onClick={handleJobClick} className="cursor-pointer p-4 pt-3">
          {/* Company Logo & Job Info */}
          <div className="mb-4 flex items-start gap-4">
            <div className="flex-shrink-0">
              <img
                className="h-12 w-12 object-contain"
                src="/img/landing/logo.svg"
                alt="Company Logo"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-semibold leading-tight text-[#131517] transition-colors duration-300 hover:text-primary-yes">
                {job.title}
              </h3>
              <p className="mt-1 text-sm text-gray-600">YES Partner</p>
            </div>
          </div>

          {/* Job Skills */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {job &&
                job.job_skills &&
                job.job_skills.slice(0, 3).map((skill: any, index: any) => (
                  <span
                    className="inline-block rounded-xl bg-[#FFF4F5] px-3 py-1 text-xs"
                    key={index}
                  >
                    {skill.name}
                  </span>
                ))}
              {job.job_skills && job.job_skills.length > 3 && (
                <span className="inline-block rounded-xl bg-gray-100 px-3 py-1 text-xs text-gray-600">
                  +{job.job_skills.length - 3}
                </span>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="mb-4 flex items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0 text-primary-yes" />
            <span className="text-sm font-medium text-gray-700">
              {job.country.name}
            </span>
          </div>

          {/* Salary */}
          <div className="mb-4">
            <p className="font-semibold text-gray-900">
              IDR{' '}
              {`${toIdrWithoutRp(job?.min ?? 0)} - ${toIdrWithoutRp(job?.max ?? 0)}`}
            </p>
          </div>

          {/* Posted Date */}
          {job.created_at && (
            <div className="mb-4">
              <p className="text-xs text-gray-400">
                Diposting {new Date(job.created_at).toLocaleDateString('id-ID')}
              </p>
            </div>
          )}
        </div>

        {/* Bottom Section - Apply Button */}
        <div className="px-4 pb-4">
          <button
            onClick={handleApplyNow}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-yes px-4 py-2 font-medium text-white transition-all duration-300 hover:bg-primary-yes/90"
          >
            Lamar Sekarang
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}

const SavedJobsList = forwardRef<HTMLDivElement, SavedJobsListProps>(
  ({ id }, ref) => {
    // Query job list with bookmark status
    const {
      data: dataJobListing,
      refetch: refetchJobListing,
      isLoading: isLoadingJobs,
    } = useQuery(
      queryJobList(true) // Fetch with bookmark status
    )

    const [isDialogErrorOpen, setIsDialogErrorOpen] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [savedJobs, setSavedJobs] = useState<EnhancedJob[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [filteredSavedJobs, setFilteredSavedJobs] = useState<EnhancedJob[]>(
      []
    )

    const user = useAuth((s) => s.user)

    // Filter only bookmarked jobs
    useEffect(() => {
      if (dataJobListing) {
        const bookmarkedJobs = (dataJobListing as Job[])
          .filter((job) => job.is_bookmark === true)
          .map((job) => ({
            ...job,
            is_bookmark: true,
          })) as EnhancedJob[]

        setSavedJobs(bookmarkedJobs)
        setFilteredSavedJobs(bookmarkedJobs)
      }
    }, [dataJobListing])

    // Handle search within saved jobs
    const handleSearch = (searchValue: string) => {
      setSearchTerm(searchValue)

      if (searchValue.trim()) {
        const filtered = savedJobs.filter(
          (job) =>
            job.title.toLowerCase().includes(searchValue.toLowerCase()) ||
            (job.country?.name &&
              job.country.name
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            job.job_skills?.some((skill: any) =>
              skill.name.toLowerCase().includes(searchValue.toLowerCase())
            )
        )
        setFilteredSavedJobs(filtered)
      } else {
        setFilteredSavedJobs(savedJobs)
      }
    }

    // Refresh data on component mount
    useEffect(() => {
      refetchJobListing()
    }, [refetchJobListing])

    return (
      <>
        <div
          ref={ref}
          id={id}
          className="flex min-h-screen flex-col items-center px-4 py-2"
        >
          {/* Stats */}
          <div className="mb-6 w-full">
            <div className="rounded-lg border border-[#D4D4D4] bg-white p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {savedJobs.length} Pekerjaan Tersimpan
              </p>
              <p className="text-sm text-gray-600">
                {searchTerm
                  ? `Menampilkan ${filteredSavedJobs.length} hasil`
                  : 'Total pekerjaan yang Anda simpan'}
              </p>
            </div>
          </div>

          {/* Search Bar - Only show if there are saved jobs */}
          {savedJobs.length > 0 && (
            <div className="mb-8 w-full max-w-2xl">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Cari dalam pekerjaan tersimpan..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full rounded-lg border border-[#D4D4D4] py-3 pl-10 pr-4 outline-none focus:border-transparent focus:ring-2 focus:ring-primary-yes"
                />
              </div>
            </div>
          )}

          {/* Jobs Grid */}
          <div className="w-full max-w-6xl">
            {isLoadingJobs ? (
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <SavedJobCardSkeleton key={index} />
                ))}
              </div>
            ) : filteredSavedJobs.length > 0 ? (
              <>
                <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                  {filteredSavedJobs.map((job) => (
                    <SavedJobCard key={job.id} job={job} />
                  ))}
                </div>
              </>
            ) : savedJobs.length === 0 ? (
              // No saved jobs at all
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                  <Bookmark className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-gray-600">
                  Belum Ada Pekerjaan Tersimpan
                </h3>
                <p className="mb-6 text-gray-500">
                  Mulai jelajahi lowongan kerja dan simpan yang menarik untuk
                  Anda
                </p>
                <button
                  onClick={() => (window.location.href = '/')}
                  className="rounded-lg bg-primary-yes px-6 py-3 font-medium text-white transition-colors duration-300 hover:bg-primary-yes/90"
                >
                  Jelajahi Lowongan Kerja
                </button>
              </div>
            ) : (
              // No search results
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                  <Search className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-gray-600">
                  Tidak ada hasil yang ditemukan
                </h3>
                <p className="mb-4 text-gray-500">
                  Coba ubah kata kunci pencarian Anda
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('')
                    setFilteredSavedJobs(savedJobs)
                  }}
                  className="font-medium text-primary-yes hover:underline"
                >
                  Hapus filter pencarian
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }
)

SavedJobsList.displayName = 'SavedJobsList'

export default SavedJobsList
